import { Show, type Accessor, type JSX } from 'solid-js';
import type { MemPoint } from '../state/metrics';
import { HudPanel } from '../ui/hud-panel';
import { InfoTip } from '../ui/info-tip';

const TIPS = {
  swap:
    'When RAM runs out, the system parks some of it on the disk, which is hundreds of times slower. If this is above zero and rising, the whole machine will feel laggy — a sign to close something.',
  disk:
    'Free space on the drive where downloaded models are stored. Each model is several GB, so if this runs low, downloading a new one will fail partway.',
} as const;

function gb(mb: number, decimals = 1): string {
  const f = 10 ** decimals;
  return String(Math.round((mb / 1024) * f) / f);
}

function pct(used: number, total: number): number {
  return total > 0 ? Math.min(100, Math.max(0, (used / total) * 100)) : 0;
}

function Meter(props: { fillPct: number; tone?: 'holo' | 'warn' }) {
  return (
    <div class="hud-clip h-1.5 bg-track">
      <div
        class="h-full transition-[width] duration-500"
        style={{
          width: `${props.fillPct}%`,
          background: props.tone === 'warn' ? 'var(--warn)' : 'var(--holo-deep)',
        }}
      />
    </div>
  );
}

function Row(props: { label: JSX.Element; value: JSX.Element; children?: JSX.Element }) {
  return (
    <div class="mt-3">
      <div class="mb-1.5 flex items-baseline justify-between gap-2 text-xs">
        <span class="hud-mono inline-flex items-center gap-1 text-[0.625rem] uppercase tracking-[0.18em] text-faint">
          {props.label}
        </span>
        <span class="font-semibold tabular-nums">{props.value}</span>
      </div>
      {props.children}
    </div>
  );
}

export function MemoryWidget(props: { mem: Accessor<MemPoint[]> }) {
  const latest = () => props.mem().at(-1) ?? null;
  const swap = () => {
    const m = latest();
    return m && m.swapTotalMb > 0 ? m : null;
  };
  /**
   * Three distinct, honest states — never claim an ongoing action ("swapping") for
   * memory that is merely resident from a past episode: only an active page-out rate
   * earns that word. Parked pages get their own word instead of being folded into
   * either "swapping" (a lie about the present) or "untouched" (a lie about the past).
   */
  const swapState = (): 'swapping' | 'parked' | 'untouched' | null => {
    const m = latest();
    if (!m) return null;
    if ((m.swapOutPerS ?? 0) > 0) return 'swapping';
    if (m.swapUsedMb > 0) return 'parked';
    return 'untouched';
  };
  const disk = () => {
    const m = latest();
    return m && m.diskFreeMb != null ? m : null;
  };

  return (
    <HudPanel title="Memory" sub="RAM · swap · models disk">
      <Show when={latest()} fallback={<p class="text-[0.8rem] text-faint">waiting for samples…</p>}>
        {(m) => (
          <>
            <div class="mb-2 flex items-baseline gap-2">
              <b class="text-[1.75rem] font-light tabular-nums text-holo">{gb(m().usedMb)}</b>
              <span class="hud-mono text-[0.6563rem] uppercase tracking-[0.1em] text-faint">
                / {gb(m().totalMb)} GB used
              </span>
            </div>
            <Meter fillPct={pct(m().usedMb, m().totalMb)} />

            <Show when={swap()}>
              {(s) => (
                <Row
                  label={<>Swap <InfoTip text={TIPS.swap} /></>}
                  value={
                    <>
                      {s().swapUsedMb.toLocaleString('en-US')}
                      <small class="font-normal text-faint">
                        {' '}/ {s().swapTotalMb.toLocaleString('en-US')} MB · {swapState()}
                      </small>
                    </>
                  }
                >
                  <Meter
                    fillPct={pct(s().swapUsedMb, s().swapTotalMb)}
                    tone={swapState() === 'swapping' ? 'warn' : 'holo'}
                  />
                </Row>
              )}
            </Show>

            <Show when={disk()}>
              {(d) => (
                <Row
                  label={<>Models disk <InfoTip text={TIPS.disk} /></>}
                  value={
                    <>
                      {Math.round(d().diskFreeMb! / 1024)}
                      <small class="font-normal text-faint">
                        {' '}GB free
                        <Show when={d().diskTotalMb != null}>
                          {' '}of {Math.round(d().diskTotalMb! / 1024)}
                        </Show>
                      </small>
                    </>
                  }
                >
                  <Show when={d().diskTotalMb != null}>
                    <Meter fillPct={pct(d().diskTotalMb! - d().diskFreeMb!, d().diskTotalMb!)} />
                  </Show>
                </Row>
              )}
            </Show>
          </>
        )}
      </Show>
    </HudPanel>
  );
}
